import React, { useState } from 'react';

/**
 * CategoryCard — premium category display card.
 *
 * Props:
 *   category       — { category_id, name, image }
 *   isActive       — boolean
 *   colorConfig    — { grad, shadow, activeShadow, iconBg, iconText }
 *   onClick        — function(category_id)
 */
const CategoryCard = ({ category, isActive, colorConfig, onClick }) => {
  const [imgErr, setImgErr] = useState(false);

  const {
    grad = 'from-primary-400 to-primary-600',
    shadow = 'hover:shadow-primary-500/20',
    activeShadow = 'shadow-primary-500/40',
    iconBg = 'bg-primary-50',
    iconText = 'text-primary-600',
  } = colorConfig || {};

  return (
    <button
      onClick={() => onClick(category.category_id)}
      className={`group relative flex flex-col items-center justify-between overflow-hidden rounded-3xl text-center transition-all duration-400 ease-out
        ${isActive
          ? `bg-gradient-to-br ${grad} text-white shadow-2xl ${activeShadow} scale-[1.04] -translate-y-1`
          : `bg-white border border-gray-100 shadow-sm ${shadow} hover:shadow-xl hover:-translate-y-1.5 hover:border-transparent`
        }`}
    >
      {/* Subtle hover gradient overlay */}
      {!isActive && (
        <div className={`absolute inset-0 opacity-0 group-hover:opacity-[0.04] transition-opacity duration-300 bg-gradient-to-br ${grad} rounded-3xl pointer-events-none`} />
      )}

      {/* Image — full width, tall */}
      <div className={`relative w-full overflow-hidden rounded-t-3xl ${isActive ? 'bg-white/15' : iconBg}`}
           style={{ aspectRatio: '1 / 1' }}>
        {category.image && !imgErr ? (
          <img
            src={category.image}
            alt={category.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div className={`flex h-full w-full items-center justify-center`}>
            <span className={`text-4xl sm:text-5xl font-black ${isActive ? 'text-white/90' : iconText}`}>
              {category.name.charAt(0)}
            </span>
          </div>
        )}

        {/* Active glow overlay */}
        {isActive && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        )}
      </div>

      {/* Name */}
      <div className="w-full px-2 pb-3 pt-2">
        <p className={`text-xs font-bold leading-tight tracking-wide sm:text-sm truncate
          ${isActive ? 'text-white' : 'text-gray-800 group-hover:text-gray-900'}`}>
          {category.name}
        </p>
        {isActive && (
          <span className="mt-1.5 inline-block h-1 w-8 rounded-full bg-white/60" />
        )}
      </div>
    </button>
  );
};

export default CategoryCard;
